import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/dates";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCheck } from "lucide-react";

export default function NotificationBell({ variant = "" }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const sfx = variant ? `-${variant}` : "";

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); }
    setOpen(false);
    load();
    if (n.job_id) navigate(`/jobs/${n.job_id}`);
  };

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`notification-bell${sfx}`} className="relative p-2 rounded-lg hover:bg-white/10 lg:hover:bg-slate-100 transition-colors">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span data-testid={`notification-badge${sfx}`} className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <p className="font-heading font-semibold text-navy">Notifications</p>
          {data.unread > 0 && (
            <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-blue-700 flex items-center gap-1 font-medium">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <p className="text-slate-400 text-sm text-center py-8">No notifications</p>}
          {data.items.map((n) => (
            <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.read ? "bg-blue-50/50" : ""}`}>
              <div className="flex gap-2">
                {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-600 shrink-0" />}
                <div className={!n.read ? "" : "pl-4"}>
                  <p className="text-sm text-navy">{n.message}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
